import React, { useCallback, useEffect, useState } from 'react';
import { Building2, Check, Pencil, Plus, Settings, Trash2, X } from 'lucide-react';
import type { Engagement, Entity, TBAccount } from '../types';
import {
  createEntity,
  deleteEntity,
  getAccounts,
  getEntities,
  renameEntity,
} from '../db/database';
import { db } from '../db/database';

interface SettingsViewProps {
  engagement: Engagement;
}

type Tab = 'entities';

const SettingsView: React.FC<SettingsViewProps> = ({ engagement }) => {
  const [activeTab, setActiveTab] = useState<Tab>('entities');
  const [entities, setEntities] = useState<Entity[]>([]);
  const [accounts, setAccounts] = useState<TBAccount[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [newEntityName, setNewEntityName] = useState('');

  const loadData = useCallback(async () => {
    const [entityList, accountList] = await Promise.all([
      getEntities(engagement.id!),
      getAccounts(engagement.id!),
    ]);
    setEntities(entityList);
    setAccounts(accountList);
  }, [engagement.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  function hasAccounts(entityName: string): boolean {
    return accounts.some(a => a.entity === entityName);
  }

  async function handleAdd() {
    const name = newEntityName.trim();
    if (!name) return;
    if (name.toLowerCase() === 'eliminations') return;
    if (entities.some(e => e.name.toLowerCase() === name.toLowerCase())) return;
    await createEntity({ id: crypto.randomUUID(), engagementId: engagement.id!, name });
    setNewEntityName('');
    const updated = await getEntities(engagement.id!);
    const customCount = updated.filter(e => e.name.toLowerCase() !== 'eliminations').length;
    const hasEliminations = updated.some(e => e.name.toLowerCase() === 'eliminations');
    if (customCount === 2 && !hasEliminations) {
      await createEntity({ id: crypto.randomUUID(), engagementId: engagement.id!, name: 'Eliminations' });
    }
    await loadData();
  }

  function startEdit(entity: Entity) {
    setEditingId(entity.id);
    setEditingName(entity.name);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditingName('');
  }

  async function handleRename(entity: Entity) {
    const name = editingName.trim();
    if (!name || name === entity.name) { cancelEdit(); return; }
    if (entities.some(e => e.id !== entity.id && e.name.toLowerCase() === name.toLowerCase())) {
      cancelEdit();
      return;
    }
    // Rename entity record
    await renameEntity(entity.id, name);
    // Update all accounts that had the old entity name
    await db.accounts
      .where('engagementId').equals(engagement.id!)
      .and(a => a.entity === entity.name)
      .modify({ entity: name });
    cancelEdit();
    await loadData();
  }

  async function handleDelete(entity: Entity) {
    if (entity.name.toLowerCase() === 'eliminations') return;
    if (hasAccounts(entity.name)) return;
    await deleteEntity(entity.id);
    await loadData();
  }

  const tabNav = (
    <nav className="w-52 flex-shrink-0 border-r border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex flex-col">
      <div className="p-5 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-2">
          <Settings className="w-4 h-4 text-slate-400" />
          <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Settings</h2>
        </div>
      </div>
      <div className="p-3 flex-1">
        <button
          onClick={() => setActiveTab('entities')}
          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'entities'
              ? 'bg-charcoal-700 text-white'
              : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
          }`}
        >
          <Building2 className="w-4 h-4 flex-shrink-0" />
          Entities
        </button>
      </div>
    </nav>
  );

  const entitiesPanel = (
    <div className="flex-1 p-8 overflow-y-auto">
      <div className="max-w-xl">
        <h1 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-1">Entities</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
          Manage consolidation entities for <span className="font-medium">{engagement.clientName}</span>.
          Each account in the trial balance is assigned to one entity.
        </p>

        {/* Add new entity */}
        <div className="flex gap-2 mb-6">
          <input
            type="text"
            value={newEntityName}
            onChange={e => setNewEntityName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
            placeholder="New entity name…"
            className="flex-1 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleAdd}
            disabled={!newEntityName.trim()}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
        </div>

        {/* Entity list */}
        <div className="space-y-2">
          {entities.length === 0 && (
            <p className="text-sm text-slate-400 italic">No entities yet.</p>
          )}
          {entities.map(entity => {
            const isSystem = entity.name.toLowerCase() === 'eliminations';
            const locked = hasAccounts(entity.name);
            const isEditing = editingId === entity.id;

            return (
              <div
                key={entity.id}
                className="flex items-center gap-3 px-4 py-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800"
              >
                <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />

                {isEditing ? (
                  <input
                    autoFocus
                    type="text"
                    value={editingName}
                    onChange={e => setEditingName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleRename(entity);
                      if (e.key === 'Escape') cancelEdit();
                    }}
                    className="flex-1 px-2 py-1 text-sm border border-blue-400 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <span className="flex-1 text-sm text-slate-800 dark:text-slate-100">
                    {entity.name}
                    {isSystem && (
                      <span className="ml-2 text-xs text-slate-400 dark:text-slate-500 font-normal">auto</span>
                    )}
                  </span>
                )}

                <div className="flex items-center gap-1 flex-shrink-0">
                  {isEditing ? (
                    <>
                      <button
                        onClick={() => handleRename(entity)}
                        className="p-1.5 rounded hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600 transition-colors"
                        title="Save"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 transition-colors"
                        title="Cancel"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </>
                  ) : !isSystem ? (
                    <button
                      onClick={() => startEdit(entity)}
                      className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600 transition-colors"
                      title="Rename"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  ) : null}

                  <button
                    onClick={() => handleDelete(entity)}
                    disabled={locked || isEditing || isSystem}
                    title={isSystem ? 'Auto-managed by TaxTrail' : locked ? 'Cannot delete: accounts are assigned to this entity' : 'Delete entity'}
                    className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-slate-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-full flex bg-slate-50 dark:bg-slate-900">
      {tabNav}
      {activeTab === 'entities' && entitiesPanel}
    </div>
  );
};

export default SettingsView;
